import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  CreateCompanyRequest,
  CreateCompanyResponse,
  DashboardData,
  CompanyDetailsResponse,
  SimulationRequest,
  CompanyBasicInfo,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
  DeleteCompanyResponse,
  SignalCardTask,
  CreateSignalCardTaskRequest,
  CreateSignalCardTaskResponse,
  SignalCardTaskStatusResponse,
  SignalScanTask,
  CreateSignalScanTaskRequest,
  CreateSignalScanTaskResponse,
  SignalScanTaskStatusResponse,
  SignalScanTaskDetails,
  SignalScanMultiEntityTask,
  CreateSignalScanMultiEntityTaskRequest,
  CreateSignalScanMultiEntityTaskResponse,
  SignalScanMultiEntityTaskDetails,
  TransactionHistoryResponse,
  WalletBalanceResponse,
  AdminWalletsResponse,
  AdminDepositRequest,
  SignalCardDemoTasksResponse,
} from "../types";
import { ApiStatusError } from "./httpStatus";
import { attachSession } from "./session";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Use hostname to determine backend URL, fallback to environment variable or localhost
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = hostname === "localhost" ? ":8080" : "";
    
    // Debug environment variable resolution
    console.log("API Service Environment Debug:", {
      hostname,
      protocol,
      envApiBaseUrl: process.env.REACT_APP_API_BASE_URL,
      calculatedUrl: `${protocol}//${hostname}${port}`,
    });
    
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || `${protocol}//${hostname}${port}`;
      
    console.log("🌐 API Service initialized with baseURL:", baseURL);

    this.api = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      (config) => {
        // Log the request details
        console.log("🚀 API Request:", {
          method: config.method?.toUpperCase(),
          url: config.url,
          fullURL: `${config.baseURL}${config.url}`,
          headers: config.headers,
          data: config.data,
        });

        // The bearer token is added by `attachSession` below.
        return config;
      },
      (error) => {
        console.error("❌ Request interceptor error:", error);
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response) => {
        console.log("✅ API Response:", {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          fullURL: `${response.config.baseURL}${response.config.url}`,
          status: response.status,
          statusText: response.statusText,
          data: response.data,
        });
        return response;
      },
      (error) => {
        // Detailed error logging
        const errorDetails = {
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          fullURL: error.config
            ? `${error.config.baseURL}${error.config.url}`
            : "Unknown URL",
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          message: error.message,
          networkError: !error.response,
          timeout: error.code === "ECONNABORTED",
        };

        console.error("❌ API Error Details:", errorDetails);

        // A 401 is not handled here: `attachSession` holds the call while the user signs in
        // again and sends it once more, rather than sending the browser away from the page.
        return Promise.reject(error);
      },
    );

    // Node-RED's ApiAuth answers a token it turns away with a 200 whose body carries the 401.
    attachSession(this.api, {
      rejectedInBody: (data) => {
        const body = data as { status?: unknown; statusCode?: unknown } | null;
        return (
          typeof body === "object" &&
          body !== null &&
          body.status === "error" &&
          body.statusCode === 401
        );
      },
    });
  }

  async createCompany(
    data: CreateCompanyRequest,
  ): Promise<CreateCompanyResponse> {
    try {
      console.log("📤 Creating company with data:", data);
      const response: AxiosResponse<CreateCompanyResponse> =
        await this.api.post("/api/companies", data);
      console.log("✅ Company created successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to create company:", {
        requestData: data,
        error: error,
      });
      // Re-throw with better error handling. The status goes along: this is the call that
      // charges a SignalValidate run, and a 402 here means the wallet was short.
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to create company";
        throw new ApiStatusError(message, error.response?.status);
      }
      throw error;
    }
  }

  async getDashboard(): Promise<DashboardData> {
    try {
      console.log("📊 Fetching dashboard data...");
      const response: AxiosResponse<DashboardData> =
        await this.api.get("/api/dashboard");
      console.log("✅ Dashboard data fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Failed to fetch dashboard data:", error);
      // Re-throw with better error handling
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch dashboard data";
        throw new Error(message);
      }
      throw error;
    }
  }

  async getCompanyDetails(id: string): Promise<CompanyDetailsResponse> {
    try {
      console.log(`🏢 Fetching company details for ID: ${id}`);
      const response: AxiosResponse<CompanyDetailsResponse> =
        await this.api.get(`/api/companies/${id}/details`);
      console.log("✅ Company details fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch company details for ID ${id}:`, error);
      // Re-throw with better error handling
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch company details";
        throw new Error(message);
      }
      throw error;
    }
  }

  async simulateSurvey(data: SimulationRequest): Promise<void> {
    try {
      console.log("🎲 Simulating survey with data:", data);
      await this.api.post("/api/surveys/simulate", data);
      console.log("✅ Survey simulation completed successfully");
    } catch (error) {
      console.error("❌ Failed to simulate survey:", {
        requestData: data,
        error: error,
      });
      // Re-throw with better error handling
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to simulate survey";
        throw new Error(message);
      }
      throw error;
    }
  }

  async startOrRestartAnalysis(
    companyId: number,
    kind: "survey" | "interview",
  ): Promise<void> {
    try {
      console.log(
        `🔄 ${kind === "survey" ? "Starting" : "Restarting"} analysis for company ${companyId} (kind: ${kind})`,
      );
      await this.api.post("/api/analyze", { companyId, kind });
      console.log(
        `✅ Analysis ${kind === "survey" ? "started" : "restarted"} successfully for company ${companyId}`,
      );
    } catch (error) {
      console.error(
        `❌ Failed to ${kind === "survey" ? "start" : "restart"} analysis:`,
        {
          companyId,
          kind,
          error: error,
        },
      );
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          `Failed to ${kind === "survey" ? "start" : "restart"} analysis`;
        throw new Error(message);
      }
      throw error;
    }
  }

  async updateCustomerTitle(title: string): Promise<void> {
    try {
      console.log(`📝 Updating customer title to: ${title}`);
      await this.api.post("/api/customer/title", { title });
      console.log("✅ Customer title updated successfully");
    } catch (error) {
      console.error("❌ Failed to update customer title:", {
        title,
        error: error,
      });
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to update customer title";
        throw new Error(message);
      }
      throw error;
    }
  }

  async getCompany(id: string | number): Promise<CompanyBasicInfo> {
    try {
      console.log(`🏢 Fetching company info for ID: ${id}`);
      const response: AxiosResponse<CompanyBasicInfo> =
        await this.api.get(`/api/companies/${id}`);
      console.log("✅ Company info fetched successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch company info for ID ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch company information";
        throw new Error(message);
      }
      throw error;
    }
  }

  async updateCompany(
    id: string | number,
    data: UpdateCompanyRequest,
  ): Promise<UpdateCompanyResponse> {
    try {
      console.log(`📝 Updating company ID ${id} with data:`, data);
      const response: AxiosResponse<UpdateCompanyResponse> =
        await this.api.put(`/api/companies/${id}`, data);
      console.log("✅ Company updated successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update company ID ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update company";
        throw new Error(message);
      }
      throw error;
    }
  }

  async deleteCompany(id: string | number): Promise<DeleteCompanyResponse> {
    try {
      console.log(`🗑️ Deleting company ID: ${id}`);
      const response: AxiosResponse<DeleteCompanyResponse> =
        await this.api.delete(`/api/companies/${id}`);
      console.log("✅ Company deleted successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete company ID ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to delete company";
        throw new Error(message);
      }
      throw error;
    }
  }

  async downloadIPPM(): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log("📥 Downloading IPPM file...");
      const response: AxiosResponse<Blob> = await this.api.get("/api/ippm", {
        responseType: "blob",
      });

      let filename = "ippm.xlsx"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      console.log("✅ IPPM file downloaded successfully, filename:", filename);
      return { blob: response.data, filename };
    } catch (error) {
      console.error("❌ Failed to download IPPM file:", error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to download IPPM file";
        throw new Error(message);
      }
      throw error;
    }
  }

  async uploadIPPM(file: File): Promise<void> {
    try {
      console.log("📤 Uploading IPPM file:", file.name);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", file.name);
      
      const response = await this.api.post("/api/ippm", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("✅ IPPM file uploaded successfully:", response.data);
    } catch (error) {
      console.error("❌ Failed to upload IPPM file:", error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to upload IPPM file";
        throw new Error(message);
      }
      throw error;
    }
  }

  async downloadCompanyReports(id: string | number): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log(`📥 Downloading reports zip for company ID: ${id}`);
      const response: AxiosResponse<Blob> = await this.api.get(
        `/api/companies/${id}/reports/download`,
        { responseType: "blob" },
      );

      let filename = "company_reports.zip"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      console.log("✅ Reports archive downloaded successfully, filename:", filename);
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`❌ Failed to download reports for company ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to download reports";
        throw new Error(message);
      }
      throw error;
    }
  }

  async downloadCompanyLogs(id: string | number): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log(`📥 Downloading logs zip for company ID: ${id}`);
      const response: AxiosResponse<Blob> = await this.api.get(
        `/api/companies/${id}/logs/download`,
        { responseType: "blob" },
      );

      let filename = "company_logs.zip"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      console.log("✅ Logs archive downloaded successfully, filename:", filename);
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`❌ Failed to download logs for company ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to download logs";
        throw new Error(message);
      }
      throw error;
    }
  }

  async downloadParticipants(id: string | number): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log(`📥 Downloading participants for company ID: ${id}`);
      const response: AxiosResponse<Blob> = await this.api.get(
        `/api/companies/${id}/participants/download`,
        { responseType: "blob" },
      );

      let filename = "participants.zip"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      console.log("✅ Participants list downloaded successfully, filename:", filename);
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`❌ Failed to download participants for company ${id}:`, error);
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to download participants list";
        throw new Error(message);
      }
      throw error;
    }
  }

  /* ---- SignalCard Methods ---- */

  public async getSignalCardTasks(): Promise<SignalCardTask[]> {
    try {
      const response = await this.api.get<SignalCardTask[]>("/api/signal-card/tasks");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SignalCard tasks:", error);
      throw error;
    }
  }

  public async createSignalCardTask(data: CreateSignalCardTaskRequest): Promise<CreateSignalCardTaskResponse> {
    try {
      const response = await this.api.post<CreateSignalCardTaskResponse>("/api/signal-card/tasks", data);
      return response.data;
    } catch (error) {
      console.error("Failed to create SignalCard task:", error);
      throw error;
    }
  }

  public async getSignalCardTaskStatus(taskId: string): Promise<SignalCardTaskStatusResponse> {
    try {
      const response = await this.api.get<SignalCardTaskStatusResponse>(`/api/signal-card/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch status for task ${taskId}:`, error);
      throw error;
    }
  }

  public async downloadSignalCardReport(taskId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(`/api/signal-card/tasks/${taskId}/download`, {
        responseType: "blob",
        timeout: 120000
      });

      let filename = "report.pdf"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]*/g, '');
        }
      }
      
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download report for task ${taskId}:`, error);
      throw error;
    }
  }

  public async deleteSignalCardTask(taskId: string): Promise<void> {
    try {
      await this.api.delete(`/api/signal-card/tasks/${taskId}`);
    } catch (error) {
      console.error(`Failed to delete task ${taskId}:`, error);
      throw error;
    }
  }

  public async downloadSignalCardPrompt(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-card/prompt", {
        responseType: "blob",
      });

      let filename = "signal-card-prompt.txt"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalCard prompt:", error);
      throw error;
    }
  }

  public async downloadSignalCardUsage(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-card/usage", {
        responseType: "blob",
      });

      let filename = "signal-card-usage.xlsx"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalCard usage spreadsheet:", error);
      throw error;
    }
  }

  public async uploadSignalCardPrompt(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post("/api/signal-card/prompt", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Failed to upload SignalCard prompt:", error);
      throw error;
    }
  }

  public async downloadSignalCardDuplicateWarning(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/text/duplicate-warning", {
        responseType: "blob",
      });

      let filename = "signal-card-duplicate-warning.txt";
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalCard duplicate warning:", error);
      throw error;
    }
  }

  public async uploadSignalCardDuplicateWarning(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post("/api/text/duplicate-warning", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Failed to upload SignalCard duplicate warning:", error);
      throw error;
    }
  }

  public async getSignalCardDuplicateWarningText(): Promise<string | null> {
    try {
      const { blob } = await this.downloadSignalCardDuplicateWarning();
      return await blob.text();
    } catch {
      return null;
    }
  }

  /* ---- SignalScan Methods ---- */

  public async getSignalScanTasks(): Promise<SignalScanTask[]> {
    try {
      const response = await this.api.get<SignalScanTask[]>("/api/signal-scan/tasks");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SignalScan tasks:", error);
      throw error;
    }
  }

  public async getSignalScanTask(taskId: string): Promise<SignalScanTaskDetails> {
    try {
      const response = await this.api.get<SignalScanTaskDetails>(`/api/signal-scan/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch SignalScan task ${taskId}:`, error);
      throw error;
    }
  }

  public async createSignalScanTask(data: CreateSignalScanTaskRequest): Promise<CreateSignalScanTaskResponse> {
    const formData = new FormData();
    formData.append("company_name", data.company_name);
    formData.append("relationship", data.relationship);
    if (data.thesis_file) {
      formData.append("thesis_file", data.thesis_file);
    }

    try {
      const response = await this.api.post<CreateSignalScanTaskResponse>("/api/signal-scan/tasks", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to create SignalScan task:", error);
      throw error;
    }
  }

  public async getSignalScanTaskStatus(taskId: string): Promise<SignalScanTaskStatusResponse> {
    try {
      const response = await this.api.get<SignalScanTaskStatusResponse>(`/api/signal-scan/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch status for task ${taskId}:`, error);
      throw error;
    }
  }

  public async downloadSignalScanReport(taskId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(`/api/signal-scan/tasks/${taskId}/download`, {
        responseType: "blob",
        timeout: 120000
      });

      let filename = "report.pdf"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]*/g, '');
        }
      }
      
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download report for task ${taskId}:`, error);
      throw error;
    }
  }

  public async deleteSignalScanTask(taskId: string): Promise<void> {
    try {
      await this.api.delete(`/api/signal-scan/tasks/${taskId}`);
    } catch (error) {
      console.error(`Failed to delete task ${taskId}:`, error);
      throw error;
    }
  }

  public async downloadSignalScanPrompt(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-scan/prompt", {
        responseType: "blob",
        timeout: 120000
      });

      let filename = "signal-scan-prompt.txt"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalScan prompt:", error);
      throw error;
    }
  }

  public async downloadSignalScanUsage(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-scan/usage", {
        responseType: "blob",
        timeout: 120000
      });

      let filename = "signal-scan-usage.xlsx"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalScan usage spreadsheet:", error);
      throw error;
    }
  }

  public async uploadSignalScanPrompt(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post("/api/signal-scan/prompt", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Failed to upload SignalScan prompt:", error);
      throw error;
    }
  }

  public async downloadSignalScanStagePrompt(stage: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(`/api/signal-scan/${stage}/prompt`, {
        responseType: "blob",
        timeout: 120000
      });

      let filename = `signal-scan-${stage}-prompt.txt`; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download SignalScan ${stage} prompt:`, error);
      throw error;
    }
  }

  public async uploadSignalScanStagePrompt(stage: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post(`/api/signal-scan/${stage}/prompt`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error(`Failed to upload SignalScan ${stage} prompt:`, error);
      throw error;
    }
  }

  /* ---- SignalScan Multi-Entity Methods ---- */

  public async getSignalScanMultiEntityTasks(): Promise<SignalScanMultiEntityTask[]> {
    try {
      const response = await this.api.get<SignalScanMultiEntityTask[]>("/api/signal-scan-multi-entity/tasks");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SignalScan Multi-Entity tasks:", error);
      throw error;
    }
  }

  public async getSignalScanMultiEntityTask(taskId: string): Promise<SignalScanMultiEntityTaskDetails> {
    try {
      const response = await this.api.get<SignalScanMultiEntityTaskDetails>(`/api/signal-scan-multi-entity/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch SignalScan Multi-Entity task ${taskId}:`, error);
      throw error;
    }
  }

  public async createSignalScanMultiEntityTask(data: CreateSignalScanMultiEntityTaskRequest): Promise<CreateSignalScanMultiEntityTaskResponse> {
    const formData = new FormData();
    data.entities.forEach((entity) => formData.append("entities", entity));
    formData.append("relationship", data.relationship);
    if (data.thesis_file) {
      formData.append("thesis_file", data.thesis_file);
    }

    try {
      const response = await this.api.post<CreateSignalScanMultiEntityTaskResponse>("/api/signal-scan-multi-entity/tasks", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to create SignalScan Multi-Entity task:", error);
      throw error;
    }
  }

  public async downloadSignalScanMultiEntityReport(taskId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(
        `/api/signal-scan-multi-entity/tasks/${taskId}/download`,
        { responseType: "blob" },
      );

      let filename = "report.zip"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').replace(/\+/g, ' ').trim();
        }
      }
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download Multi-Entity report for task ${taskId}:`, error);
      throw error;
    }
  }

  public async deleteSignalScanMultiEntityTask(taskId: string): Promise<void> {
    try {
      await this.api.delete(`/api/signal-scan-multi-entity/tasks/${taskId}`);
    } catch (error) {
      console.error(`Failed to delete Multi-Entity task ${taskId}:`, error);
      throw error;
    }
  }

  /* ---- SignalValidate Methods ---- */

  public async downloadSignalValidatePrompt(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-validate/prompt", {
        responseType: "blob",
        timeout: 120000,
      });

      let filename = "signal-validate-prompt.txt"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalValidate prompt:", error);
      throw error;
    }
  }

  public async uploadSignalValidatePrompt(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post("/api/signal-validate/prompt", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Failed to upload SignalValidate prompt:", error);
      throw error;
    }
  }

  public async downloadSignalValidateBaselinePrompt(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get("/api/signal-validate/baseline-prompt", {
        responseType: "blob",
        timeout: 120000,
      });

      let filename = "signal-validate-baseline-prompt.txt"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error("Failed to download SignalValidate baseline prompt:", error);
      throw error;
    }
  }

  public async uploadSignalValidateBaselinePrompt(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post("/api/signal-validate/baseline-prompt", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Failed to upload SignalValidate baseline prompt:", error);
      throw error;
    }
  }

  /* ---- Wallet Methods ---- */

  public async getWalletBalance(): Promise<WalletBalanceResponse> {
    try {
      const response = await this.api.get<WalletBalanceResponse>("/api/wallet/balance");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch wallet balance:", error);
      throw error;
    }
  }

  public async getTransactionHistory(limit: number = 20, offset: number = 0): Promise<TransactionHistoryResponse> {
    try {
      const response = await this.api.get<TransactionHistoryResponse>("/api/wallet/history", {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch transaction history:", error);
      throw error;
    }
  }

  public async getAdminWallets(limit: number = 50, offset: number = 0): Promise<AdminWalletsResponse> {
    try {
      const response = await this.api.get<AdminWalletsResponse>("/api/admin/wallet/users", {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch admin wallets:", error);
      throw error;
    }
  }

  public async depositCredits(data: AdminDepositRequest): Promise<void> {
    try {
      await this.api.post("/api/admin/wallet/deposit", data);
    } catch (error) {
      console.error("Failed to deposit credits:", error);
      throw error;
    }
  }

  public async getSignalCardDemoTasks(limit: number = 50, offset: number = 0): Promise<SignalCardDemoTasksResponse> {
    try {
      const response = await this.api.get<SignalCardDemoTasksResponse>("/api/manual/signal-card/tasks", {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch manual tasks:", error);
      throw error;
    }
  }

  public async downloadSignalCardDemoTaskReport(taskId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(
        `/api/manual/signal-card/tasks/${taskId}/download`,
        { responseType: "blob", timeout: 120000 },
      );

      let filename = "report.pdf"; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download report for task ${taskId}:`, error);
      throw error;
    }
  }

  public async cancelSignalCardDemoTask(taskId: string): Promise<void> {
    try {
      await this.api.post(`/api/manual/signal-card/tasks/${taskId}/cancel`);
    } catch (error) {
        console.error(`Failed to cancel task ${taskId}:`, error);
        throw error;
    }
  }

  public async deleteSignalCardDemoTask(taskId: string): Promise<void> {
    try {
      await this.api.delete(`/api/manual/signal-card/tasks/${taskId}`);
    } catch (error) {
        console.error(`Failed to delete task ${taskId}:`, error);
        throw error;
    }
  }

  public async approveSignalCardDemoTask(taskId: string): Promise<void> {
    try {
      await this.api.post(`/api/manual/signal-card/tasks/${taskId}/send`, {});
    } catch (error) {
        console.error(`Failed to approve task ${taskId}:`, error);
        throw error;
    }
  }

  public async uploadSignalCardDemoTaskReport(taskId: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post(`/api/manual/signal-card/tasks/${taskId}/update`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error(`Failed to upload report for task ${taskId}:`, error);
      throw error;
    }
  }

  /* ---- SignalScan Multi-Entity Stage Prompt Methods ---- */

  public async downloadSignalScanMultiEntityStagePrompt(stage: number): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(`/api/signal-scan-multi-entity/stage${stage}/prompt`, {
        responseType: "blob",
        timeout: 120000
      });

      let filename = `signal-scan-multi-entity-stage${stage}-prompt.txt`; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download SignalScan Multi-Entity Stage${stage} prompt:`, error);
      throw error;
    }
  }

  public async uploadSignalScanMultiEntityStagePrompt(stage: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post(`/api/signal-scan-multi-entity/stage${stage}/prompt`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error(`Failed to upload SignalScan Multi-Entity Stage${stage} prompt:`, error);
      throw error;
    }
  }

  public async downloadSignalScanMultiEntityGammaPartPrompt(part: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response: AxiosResponse<Blob> = await this.api.get(`/api/signal-scan-multi-entity/gamma_part_${part}/prompt`, {
        responseType: "blob",
        timeout: 120000
      });

      let filename = `signal-scan-multi-entity-gamma-part-${part}-prompt.txt`; // default fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error(`Failed to download SignalScan Multi-Entity Gamma Part ${part} prompt:`, error);
      throw error;
    }
  }

  public async uploadSignalScanMultiEntityGammaPartPrompt(part: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      await this.api.post(`/api/signal-scan-multi-entity/gamma_part_${part}/prompt`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error(`Failed to upload SignalScan Multi-Entity Gamma Part ${part} prompt:`, error);
      throw error;
    }
  }
  public async getSignalValidateNotifyEmail(): Promise<string> {
    try {
      const response = await this.api.get<{ email: string }>('/api/signal-validate/notify-email');
      return response.data.email ?? '';
    } catch (error) {
      console.error('Failed to get SignalValidate notify email:', error);
      throw error;
    }
  }

  public async setSignalValidateNotifyEmail(email: string): Promise<void> {
    try {
      await this.api.post('/api/signal-validate/notify-email', { email });
    } catch (error) {
      console.error('Failed to set SignalValidate notify email:', error);
      throw error;
    }
  }

  /* ---- User Account Methods ---- */

  public async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await this.api.post('/api/user/update-password', {
        currentPassword,
        newPassword,
      });
    } catch (error) {
      console.error('Failed to update password:', error);
      throw error;
    }
  }
}


export const apiService = new ApiService();
